// numberedPick — choosing one item from a long list, by typing.
//
// The ticket form offers 46 issue types in a plain dropdown. Staff scroll it
// under time pressure while a customer waits, and "Ekart Delivery Delay",
// "Ekart Shipment Not Picked" and "Ekart Out For Delivery Too Long" sit three
// apart. Two ways in, both faster than scrolling:
//
//   · type a NUMBER — every option carries a permanent one, so "23" is always
//     the same issue type and becomes muscle memory;
//   · type WORDS — forgiving of spelling, word order and partial words.
//
// The number is the item's position in the FULL list, never in the filtered
// view, or it would change as you type and select the wrong thing.

const norm = (s) => String(s || '').toLowerCase().trim();

/** Levenshtein, capped — we only care whether it is a near miss. */
export const editDistance = (a, b, cap = 3) => {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > cap) return cap + 1;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        const cur = [i];
        let best = i;
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
            if (cur[j] < best) best = cur[j];
        }
        if (best > cap) return cap + 1;
        prev = cur;
    }
    return prev[b.length];
};

/** Do the characters of `q` appear in order inside `text`? ("ekdl" → "Ekart Delay") */
export const isSubsequence = (q, text) => {
    let i = 0;
    for (const ch of text) { if (ch === q[i]) i++; if (i === q.length) return true; }
    return q.length === 0;
};

/**
 * Score one option against what was typed. Higher is better; 0 means no match.
 */
export const scoreOption = (label, query) => {
    const L = norm(label);
    const q = norm(query);
    if (!q) return 1;                                   // everything shows
    if (L === q) return 1000;
    if (L.startsWith(q)) return 900 - L.length;
    if (L.includes(q)) return 800 - L.indexOf(q);

    const words = L.split(/[^a-z0-9]+/).filter(Boolean);
    const terms = q.split(/\s+/).filter(Boolean);

    // Every typed word matches the start of some word in the label.
    if (terms.every((t) => words.some((w) => w.startsWith(t)))) return 700;

    // Every typed word is a near miss of some word — the typo case
    // ("delevery" → "delivery", "ekrat" → "ekart").
    //
    // Tolerance scales with length: two edits on a three-letter word turns
    // "ndr" into "not", which matched "Ekart Shipment NOT Picked" above the
    // "Ekart NDR" the agent actually wanted. Short words get one edit only.
    const maxEdits = (t) => (t.length <= 4 ? 1 : 2);
    if (terms.every((t) => words.some((w) => t.length >= 3 && editDistance(t, w, maxEdits(t)) <= maxEdits(t)))) return 600;

    // Initials or a run of letters in order ("ekdl").
    if (terms.length === 1 && isSubsequence(q, L)) return 400;

    return 0;
};

/**
 * Filter and rank a list for what has been typed.
 *
 * @param {Array<{value:string,label:string,number:number}>} options
 * @param {string} query
 * @returns {{rows:Array, exactNumber:object|null}}
 */
export const pickMatches = (options, query) => {
    const q = String(query || '').trim();

    // A bare number selects by its permanent position.
    if (/^\d+$/.test(q)) {
        const n = Number(q);
        const hit = options.find((o) => o.number === n) || null;
        // Still show every option whose number STARTS with what was typed, so
        // typing "1" offers 1, 10-19 rather than jumping straight to 1.
        const rows = options.filter((o) => String(o.number).startsWith(q));
        return { rows: rows.length ? rows : (hit ? [hit] : []), exactNumber: hit };
    }

    const rows = options
        .map((o) => ({ o, s: scoreOption(o.label, q) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s || a.o.number - b.o.number)
        .map((x) => x.o);
    return { rows, exactNumber: null };
};

/** Give every option a permanent number, 1-based, in list order. */
export const numberOptions = (labels) =>
    labels.map((l, i) => (typeof l === 'string'
        ? { value: l, label: l, number: i + 1 }
        : { value: l.value, label: l.label ?? l.value, number: i + 1 }));
